import IUser from '../model/IUser';

/**
 * Framework-agnostic session store.
 *
 * The access token lives in memory only — never in localStorage — so an XSS
 * bug cannot read a long-lived credential out of the browser. Session
 * continuity across reloads comes from the httpOnly refresh cookie the server
 * sets, which JavaScript cannot touch.
 *
 * `AuthProvider` mirrors this store into React state; `DataService` reads it
 * directly, which keeps the ported class components free of hooks.
 */

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');

export interface ISessionState {
    user?: IUser;
    accessToken?: string;
    /** True until the initial silent refresh has settled. */
    initializing: boolean;
}

export class AuthError extends Error {
    public readonly status: number;
    public readonly code: string;

    public constructor(message: string, status: number, code: string) {
        super(message);
        this.name = 'AuthError';
        this.status = status;
        this.code = code;
    }
}

type Listener = (state: ISessionState) => void;

function authUrl(path: string): string {
    return `${API_BASE}/auth${path}`;
}

async function readError(response: Response, fallback: string): Promise<AuthError> {
    let message = fallback;
    let code = 'error';

    try {
        const body = await response.json();
        message = body?.message ?? message;
        code = body?.error ?? code;
    } catch {
        // Non-JSON error body — keep the fallback message.
    }

    return new AuthError(message, response.status, code);
}

class SessionStore {
    private state: ISessionState = { initializing: true };
    private readonly listeners = new Set<Listener>();
    /** In-flight refresh, shared so concurrent 401s trigger only one call. */
    private refreshPromise?: Promise<string | undefined>;

    public getState(): ISessionState {
        return this.state;
    }

    public getAccessToken(): string | undefined {
        return this.state.accessToken;
    }

    public isAuthenticated(): boolean {
        return !!this.state.accessToken && !!this.state.user;
    }

    public subscribe(listener: Listener): () => void {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    }

    private setState(patch: Partial<ISessionState>): void {
        this.state = { ...this.state, ...patch };
        this.listeners.forEach((listener) => listener(this.state));
    }

    public async login(username: string, password: string): Promise<IUser> {
        const response = await fetch(authUrl('/login'), {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, password }),
        });

        if (!response.ok) {
            throw await readError(response, 'Не удалось войти. Попробуйте снова.');
        }

        const data = await response.json();
        this.setState({ user: data.user, accessToken: data.accessToken, initializing: false });
        return data.user as IUser;
    }

    public async logout(): Promise<void> {
        try {
            await fetch(authUrl('/logout'), { method: 'POST', credentials: 'include' });
        } catch {
            // A failed logout call still clears the local session.
        }
        this.setState({ user: undefined, accessToken: undefined, initializing: false });
    }

    /**
     * Exchanges the refresh cookie for a fresh access token.
     * Returns undefined when there is no valid session.
     */
    public refresh(): Promise<string | undefined> {
        if (this.refreshPromise) {
            return this.refreshPromise;
        }

        this.refreshPromise = (async () => {
            try {
                const response = await fetch(authUrl('/refresh'), {
                    method: 'POST',
                    credentials: 'include',
                });

                if (!response.ok) {
                    this.setState({ user: undefined, accessToken: undefined, initializing: false });
                    return undefined;
                }

                const data = await response.json();
                this.setState({ user: data.user, accessToken: data.accessToken, initializing: false });
                return data.accessToken as string;
            } catch {
                this.setState({ user: undefined, accessToken: undefined, initializing: false });
                return undefined;
            } finally {
                this.refreshPromise = undefined;
            }
        })();

        return this.refreshPromise;
    }

    /** Called once on app start to restore a session from the refresh cookie. */
    public async initialize(): Promise<void> {
        await this.refresh();
        if (this.state.initializing) {
            this.setState({ initializing: false });
        }
    }
}

export const session = new SessionStore();
export const apiBaseUrl = API_BASE;
